import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Checkout.css';
import { useAuth } from '../../contexts/AuthContext';
import {
    collection, query, where, getDocs, onSnapshot,
    doc, getDoc, updateDoc, serverTimestamp, runTransaction, increment, deleteDoc
} from 'firebase/firestore';
import { db, functions } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { updateOrderStatus, writeNotification, ORDER_STATUS } from '../../firebase/orders';
import { FALLBACK_SHIRT_SIZE_PRICES } from '../../firebase/products';
import {
    getDeliveryCoverageLabel,
    getPhilippinesBarangays,
    getPhilippinesCities,
    getPhilippinesProvinces,
    getPhilippinesRegions,
    isAddressWithinDeliveryCoverage,
    normalizePhilippinesAddress,
} from '../../utils/metroManilaLocations';

const NO_COLOR_SUBCATEGORIES = ['Polo Shirt', 'Jersey'];
const CLOTHING_SUBCATEGORIES = ['T-Shirt', 'Polo Shirt', 'Jersey'];
function isGenericProduct(item) {
    const sub = item?.subcategory;
    if (!sub) return false;
    return !CLOTHING_SUBCATEGORIES.includes(sub);
}

const IMPRINT_RECT = { 'Full Front':150,'Medium Front':120,'Center Chest':50,'Across Chest':80,'Left Chest':50,'Right Chest':50,'Left Vertical':120,'Right Vertical':120,'Bottom Left':50,'Bottom Right':50,'Full Back':150,'Medium Back':120,'Locker Center Patch':50,'Across Shoulder':80,'Left Sleeve':40,'Right Sleeve':40 };
const IMPRINT_CIRC = { 'Full Front':120,'Medium Front':80,'Center Chest':50,'Across Chest':80,'Left Chest':40,'Right Chest':40,'Left Vertical':100,'Right Vertical':100,'Bottom Left':50,'Bottom Right':50,'Full Back':120,'Medium Back':80,'Locker Center Patch':50,'Back Center':80,'Left Sleeve':40,'Right Sleeve':40 };
const WELCOME_PROMO = {
    id: 'welcome-15',
    promoCode: 'WELCOME15',
    promotionName: 'First Order 15% Off',
    promotionType: 'percentage',
    discountValue: 15,
    discountedProducts: 'All Products',
    source: 'system',
};
const PROMO_ALL_KEYS = new Set(['all', 'allproducts', 'allitems', 'everything', 'sitewide']);
const CHECKOUT_ELIGIBLE_STATUSES = new Set(
    [ORDER_STATUS.APPROVED].map(status => String(status || '').trim().toLowerCase())
);

function toPromoKey(value) {
    return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}

function getPromoTargets(promo) {
    return String(promo?.discountedProducts || '')
        .split(',')
        .map((segment) => segment.trim())
        .filter(Boolean);
}

function promoAppliesToItem(promo, item) {
    const targets = getPromoTargets(promo);
    if (!targets.length) return true;
    const targetKeys = targets.map(toPromoKey).filter(Boolean);
    if (targetKeys.some((key) => PROMO_ALL_KEYS.has(key))) return true;

    const itemKeys = [
        item?.type,
        item?.name,
        item?.category,
        item?.subcategory,
        item?.subCategory,
    ].map(toPromoKey).filter(Boolean);

    return targetKeys.some((key) => (
        itemKeys.some((itemKey) => itemKey === key || itemKey.includes(key) || key.includes(itemKey))
    ));
}

function getPromoEligibleItems(promo, items) {
    if (!promo) return [];
    const targets = getPromoTargets(promo);
    if (!targets.length) return items;
    const targetKeys = targets.map(toPromoKey).filter(Boolean);
    if (targetKeys.some((key) => PROMO_ALL_KEYS.has(key))) return items;
    return items.filter((item) => promoAppliesToItem(promo, item));
}

function isPromoCurrentlyAvailable(promo, now = new Date()) {
    if (!promo) return false;
    const status = String(promo.status || '').toLowerCase();
    if (!['active', 'scheduled'].includes(status)) return false;

    const startDate = promo.startDate?.toDate?.() || (promo.startDate ? new Date(promo.startDate) : null);
    const endDate = promo.endDate?.toDate?.() || (promo.endDate ? new Date(promo.endDate) : null);

    if (startDate instanceof Date && !Number.isNaN(startDate.getTime()) && now < startDate) return false;
    if (endDate instanceof Date && !Number.isNaN(endDate.getTime()) && now > endDate) return false;
    return true;
}

function calcItemTotal(item) {
    // Generic / non-clothing products: flat quantity × price
    if (isGenericProduct(item)) {
        return (Number(item.quantity) || 0) * (Number(item.price) || 0);
    }

    const quantities = item.quantities || {};
    const totalQty   = Object.values(quantities).reduce((s, q) => s + (q || 0), 0);
    if (totalQty === 0) return 0;

    // ── Polo Shirt / Jersey ───────────────────────────────────────────────────
    if (NO_COLOR_SUBCATEGORIES.includes(item.subcategory)) {
        const sizePrices = item.sizePrices && typeof item.sizePrices === 'object'
            ? item.sizePrices
            : {};
        const hasSizePrices = Object.keys(sizePrices).length > 0;

        if (hasSizePrices) {
            return Object.entries(quantities).reduce(
                (sum, [size, qty]) => sum + (qty || 0) * (Number(sizePrices[size]) || Number(item.price) || 0),
                0
            );
        }

        return totalQty * (Number(item.price) || 0);
    }

    // ── T-Shirt ───────────────────────────────────────────────────────────────
    const sizePrices = (item.sizePrices && Object.keys(item.sizePrices).length > 0)
        ? item.sizePrices
        : (FALLBACK_SHIRT_SIZE_PRICES[item.type] || {});
    const colorSurcharge = Number(item.colorSurcharge || 0);

    const shirtSubtotal = Object.entries(quantities).reduce(
        (sum, [size, qty]) => sum + (qty || 0) * ((Number(sizePrices[size]) || Number(item.price) || 0) + colorSurcharge),
        0
    );

    const shape  = (item.designShape||'').toLowerCase()==='circle'?'circle':'rectangle';
    const table  = shape==='circle'?IMPRINT_CIRC:IMPRINT_RECT;
    const locs   = (item.printLocation||'').split(',').map(s=>s.trim()).filter(Boolean);
    const prices = locs.map(l=>table[l]||0).sort((a,b)=>b-a);
    const imprintPerPc = prices.reduce((s,p,i)=>s+(i===0?p:Math.round(p*0.5)),0);
    return shirtSubtotal + imprintPerPc * totalQty;
}

const SIZE_LABELS = { xxs:'XXS', xs:'XS', small:'Small', medium:'Medium', large:'Large', xl:'XL', xxl:'2XL', xxxl:'3XL', xxxxl:'4XL', xxxxxl:'5XL' };
const SIZE_ORDER  = ['xxs','xs','small','medium','large','xl','xxl','xxxl','xxxxl','xxxxxl'];

function formatAddress(addr) {
    if (!addr) return '';
    return [addr.houseNo, addr.barangay, addr.city, addr.region || addr.province, addr.zipCode]
        .filter(Boolean).join(', ')
        + (addr.landmark ? ` (Near: ${addr.landmark})` : '');
}

function parseDeliveryFee(value) {
    if (value === null || value === undefined) return 0;
    const raw = String(value).trim();
    if (!raw) return 0;
    const lower = raw.toLowerCase();
    if (lower.includes('free')) return 0;
    const numeric = Number(raw.replace(/[^0-9.]/g, ''));
    return Number.isFinite(numeric) ? numeric : 0;
}

function formatDeliveryFeeLabel(value) {
    const raw = String(value ?? '').trim();
    if (!raw) return '₱ 0';
    if (raw.toLowerCase().includes('free')) return 'Free';
    const numeric = Number(raw.replace(/[^0-9.]/g, ''));
    if (Number.isFinite(numeric) && numeric > 0) {
        return `₱ ${numeric.toLocaleString()}`;
    }
    return raw;
}

function buildDeliveryNote(method) {
    if (!method) return '';
    const parts = [];
    if (method.fee) parts.push(formatDeliveryFeeLabel(method.fee));
    if (method.estimatedTime) parts.push(method.estimatedTime);
    if (method.coverageLabel || method.coverageArea) parts.push(method.coverageLabel || method.coverageArea);
    return parts.join(' • ');
}

function getDeliveryMethodDisplayName(method) {
    if (!method) return '';
    const name = String(method.name || '').trim();
    const courierName = String(method.courierName || '').trim();
    if (!courierName) return name;
    if (!name) return courierName;
    if (name.toLowerCase().includes(`(${courierName.toLowerCase()})`)) return name;
    return `${name} (${courierName})`;
}

const PAYMONGO_PAYMENT = {
    id: 'paymongo',
    name: 'PayMongo',
    type: 'paymongo',
    note: 'Secure checkout · GCash, Maya, cards & e-wallets',
};

function getOrderItemCount(items) {
    return (items || []).reduce((sum, item) => {
        const quantities = item?.quantities || {};
        const qtyFromSizes = Object.values(quantities).reduce((s, q) => s + (q || 0), 0);
        return sum + (qtyFromSizes > 0 ? qtyFromSizes : Number(item?.quantity || 0));
    }, 0);
}

function normalizeDeliveryMethod(method) {
    const id = method.id || method.name;
    const lowerName = String(method.name || '').toLowerCase();
    const vehicleType = method.vehicleType
        || (lowerName.includes('4-wheel') || lowerName.includes('4 wheel') ? 'four_wheel' : '')
        || (lowerName.includes('motorcycle') || lowerName.includes('rider') || lowerName.includes('courier') ? 'motorcycle' : '')
        || (lowerName.includes('pickup') ? 'pickup' : 'custom');

    return {
        ...method,
        id,
        vehicleType,
        courierName: method.courierName || '',
        displayName: getDeliveryMethodDisplayName(method),
        coverageType: method.coverageType || 'all',
        coverageValue: method.coverageValue || '',
        coverageLabel: getDeliveryCoverageLabel(method.coverageType, method.coverageValue),
    };
}

export default function Checkout() {
    const navigate               = useNavigate();
    const routerLocation         = useLocation();
    const { currentUser, getUserProfile } = useAuth();

    const incomingOrderId = routerLocation.state?.orderId || null;

    const [order,        setOrder]        = useState(null);
    const [orderLoading, setOrderLoading] = useState(true);
    const [orderError,   setOrderError]   = useState('');

    const [storeSettings, setStoreSettings] = useState({
        storeName: '',
        deliveryMethods: [],
        deliveryConfig: {
            maxItemsPerOrder: 50,
            fourWheelItemThreshold: 50,
        },
    });
    const [storeSettingsLoaded, setStoreSettingsLoaded] = useState(false);

    const [savedAddresses, setSavedAddresses] = useState([]);
    const [editingAddress, setEditingAddress] = useState(false);
    const [address, setAddress] = useState({
        houseNo:'', barangay:'', city:'', region:'', province:'', zipCode:'', landmark:''
    });
    const [regionOptions, setRegionOptions] = useState([]);
    const [provinceOptions, setProvinceOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [barangayOptions, setBarangayOptions] = useState([]);
    const [addressCovered, setAddressCovered] = useState(true);

const [deliveryMethod, setDeliveryMethod] = useState('');

    const [promoInput,    setPromoInput]    = useState('');
    const [promoLoading,  setPromoLoading]  = useState(false);
    const [promoError,    setPromoError]    = useState('');
    const [appliedPromo,  setAppliedPromo]  = useState(null);

    // ── First-order discount ─────────────────────────────────────────────────
    const [firstOrderEligible, setFirstOrderEligible] = useState(false);  // ── First-order discount
    const [firstOrderChecked,  setFirstOrderChecked]  = useState(false);
    const [firstOrderUsed,     setFirstOrderUsed]     = useState(false);

    const [sizeBreakdownOpen, setSizeBreakdownOpen] = useState(null);
    const [submitting,        setSubmitting]         = useState(false);
    const [submitError,       setSubmitError]        = useState('');

    useEffect(() => {
        const loadStoreSettings = async () => {
            try {
                const snap = await getDoc(doc(db, 'settings', 'store'));
                if (snap.exists()) {
                    const data = snap.data() || {};
                    setStoreSettings({
                        storeName: data.storeName || '',
                        deliveryMethods: data.deliveryMethods || [],
                        deliveryConfig: {
                            maxItemsPerOrder: Number(data.deliveryConfig?.maxItemsPerOrder || data.orderLimits?.maxItemsPerOrder || 50) || 50,
                            fourWheelItemThreshold: Number(data.deliveryConfig?.fourWheelItemThreshold || data.orderLimits?.fourWheelItemThreshold || 50) || 50,
                        },
                    });
                }
            } catch (err) {
                console.error('Failed to load store settings:', err);
            } finally {
                setStoreSettingsLoaded(true);
            }
        };
        loadStoreSettings();
    }, []);

    useEffect(() => {
        if (!incomingOrderId) {
            setOrderError('No order ID provided. Please access checkout from your notification or purchase history.');
            setOrderLoading(false);
            return;
        }
        const unsubscribe = onSnapshot(query(collection(db, 'orders'), where('orderId', '==', incomingOrderId)), (snap) => {
            setOrderLoading(true);
            try {
                if (snap.empty) {
                    setOrderError(`Order ${incomingOrderId} not found.`);
                } else {
                    const d = snap.docs[0];
                    const orderData = { firestoreId: d.id, ...d.data() };
                    const orderStatus = String(orderData.status || '').trim().toLowerCase();

                    if (!CHECKOUT_ELIGIBLE_STATUSES.has(orderStatus)) {
                        // If order is already in production or awaiting payment, it means payment went through or was processed.
                        // Automatically redirect to confirmation page!
                        if (orderStatus === String(ORDER_STATUS.IN_PRODUCTION || '').trim().toLowerCase() || 
                            orderStatus === String(ORDER_STATUS.AWAITING_PAYMENT || '').trim().toLowerCase() ||
                            orderData.paymentStatus === 'paid') {
                            
                            navigate('/order-confirmation', {
                                state: {
                                    orderId: orderData.orderId,
                                    orderFirestoreId: orderData.firestoreId,
                                    orderItems: orderData.orderItems || [],
                                    grandTotal: orderData.orderTotal || orderData.total || 0,
                                    paymentMethod: orderData.paymentMethod,
                                    estimatedDeliveryDate: orderData.estimatedDeliveryDate || '',
                                    newStatus: orderData.status,
                                    checkoutUrl: orderData.paymongoCheckoutUrl || ''
                                }
                            });
                            return;
                        }
                        
                        setOrderError(`Order ${incomingOrderId} is no longer available for checkout. Please view it in Purchase History.`);
                    } else {
                        setOrder(orderData);
                        setOrderError(''); // clear any errors if it becomes eligible again somehow
                    }
                }
            } catch (err) {
                setOrderError('Failed to load order: ' + err.message);
            }
            setOrderLoading(false);
        }, (err) => {
            setOrderError('Failed to listen to order: ' + err.message);
            setOrderLoading(false);
        });

        return () => unsubscribe();
    }, [incomingOrderId, navigate]);

    useEffect(() => {
        let isMounted = true;
        getPhilippinesRegions().then((regions) => {
            if (isMounted) setRegionOptions(regions);
        }).catch((err) => {
            console.error('Failed to load Philippine regions:', err);
        });
        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        if (!currentUser) return;
        const loadProfile = async () => {
            const profile    = await getUserProfile(currentUser.uid);
            const saved      = profile?.savedAddresses || [];
            setSavedAddresses(saved);
            const defaultAddr = saved.find(a => a.isDefault) || saved[0] || null;
            if (defaultAddr) {
                setAddress(normalizePhilippinesAddress({
                    houseNo:  defaultAddr.houseNo  || '',
                    barangay: defaultAddr.barangay || '',
                    city:     defaultAddr.city     || '',
                    region:   defaultAddr.region   || '',
                    province: defaultAddr.province || '',
                    zipCode:  defaultAddr.zipCode  || '',
                    landmark: defaultAddr.landmark || '',
                }));
            }

            // ── Check first-order eligibility ────────────────────────────────
            // Missing field or explicitly false → eligible
            setFirstOrderUsed(profile?.hasUsedFirstOrderDiscount === true);
        };
        loadProfile();
    }, [currentUser, getUserProfile]);

    useEffect(() => {
        let isMounted = true;
        if (!address.region) {
            setProvinceOptions([]);
            return undefined;
        }
        getPhilippinesProvinces(address.region).then((provinces) => {
            if (isMounted) setProvinceOptions(provinces);
        }).catch((err) => {
            console.error('Failed to load Philippine provinces:', err);
        });
        return () => { isMounted = false; };
    }, [address.region]);

    useEffect(() => {
        let isMounted = true;
        if (!address.region || !address.province) {
            setCityOptions([]);
            return undefined;
        }
        getPhilippinesCities(address.region, address.province).then((cities) => {
            if (isMounted) setCityOptions(cities);
        }).catch((err) => {
            console.error('Failed to load Philippine cities:', err);
        });
        return () => { isMounted = false; };
    }, [address.region, address.province]);

    useEffect(() => {
        let isMounted = true;
        if (!address.region || !address.province || !address.city) {
            setBarangayOptions([]);
            return undefined;
        }
        getPhilippinesBarangays(address.region, address.province, address.city).then((barangays) => {
            if (isMounted) setBarangayOptions(barangays);
        }).catch((err) => {
            console.error('Failed to load Philippine barangays:', err);
        });
        return () => { isMounted = false; };
    }, [address.region, address.province, address.city]);

    useEffect(() => {
        if (!currentUser || !order?.firestoreId) {
            setFirstOrderEligible(false);
            setFirstOrderChecked(false);
            return;
        }

        let isMounted = true;
        const checkFirstOrder = async () => {
            setFirstOrderChecked(false);
            try {
                const orderAlreadyTagged = Boolean(
                    order?.firstOrderDiscount || order?.promoCode === WELCOME_PROMO.promoCode
                );

                if (firstOrderUsed && !orderAlreadyTagged) {
                    if (isMounted) {
                        setFirstOrderEligible(false);
                        setFirstOrderChecked(true);
                    }
                    return;
                }

                const ordersSnap = await getDocs(query(collection(db, 'orders'), where('userId', '==', currentUser.uid)));
                const otherOrders = ordersSnap.docs.filter((snapshot) => snapshot.id !== order.firestoreId);
                const hasPriorNonCancelledOrder = otherOrders.some((snapshot) => {
                    const status = String(snapshot.data()?.status || '').trim().toLowerCase();
                    return status !== ORDER_STATUS.CANCELLED;
                });

                if (isMounted) {
                    setFirstOrderEligible(orderAlreadyTagged || (!firstOrderUsed && !hasPriorNonCancelledOrder));
                }
            } catch (err) {
                console.error('Failed to check first order eligibility:', err);
                if (isMounted) setFirstOrderEligible(false);
            } finally {
                if (isMounted) setFirstOrderChecked(true);
            }
        };

        checkFirstOrder();
        return () => { isMounted = false; };
    }, [currentUser, order?.firestoreId, order?.firstOrderDiscount, order?.promoCode, firstOrderUsed]);

    // ── Poll for PayMongo Payment Success ─────────────────────────────────────
    useEffect(() => {
        if (!order?.firestoreId || !functions) return;
        const status = String(order.paymentStatus || '').toLowerCase();
        const linkId = String(order.paymongoLinkId || '').trim();
        
        if (status !== 'pending' || !linkId) return;

        let intervalId = setInterval(async () => {
            try {
                const syncFn = httpsCallable(functions, 'syncPaymongoPayment');
                await syncFn({ orderFirestoreId: order.firestoreId });
            } catch (err) {
                console.warn('Checkout payment polling error:', err);
            }
        }, 5000);

        return () => clearInterval(intervalId);
    }, [order?.firestoreId, order?.paymentStatus, order?.paymongoLinkId]);

    const handleApplyPromo = async () => {
        const code = promoInput.trim().toUpperCase();
        if (!code) { setPromoError('Please enter a promo code.'); return; }

        setPromoLoading(true);
        setPromoError('');
        setAppliedPromo(null);

        try {
            const q    = query(collection(db, 'promotions'), where('promoCode', '==', code));
            const snap = await getDocs(q);

            if (snap.empty) {
                setPromoError('Promo code not found.');
                setPromoLoading(false);
                return;
            }

            const promoDoc  = snap.docs[0];
            const promoData = { id: promoDoc.id, ...promoDoc.data() };

            if (!['active', 'scheduled'].includes(String(promoData.status || '').toLowerCase())) {
                setPromoError('This promo code is no longer active.');
                setPromoLoading(false);
                return;
            }

            const now       = new Date();
            const startDate = promoData.startDate?.toDate?.() || new Date(promoData.startDate);
            const endDate   = promoData.endDate?.toDate?.()   || new Date(promoData.endDate);

            if (now < startDate) {
                setPromoError('This promo code is not yet active.');
                setPromoLoading(false);
                return;
            }
            if (now > endDate) {
                setPromoError('This promo code has expired.');
                setPromoLoading(false);
                return;
            }

            const limitType = promoData.usageLimitType || 'unlimited';
            const limitValue = promoData.usageLimitValue ?? null;

            if (limitType === 'total' && limitValue !== null) {
                const totalUsed = promoData.totalUsageCount ?? 0;
                if (totalUsed >= limitValue) {
                    setPromoError('This promo code has reached its maximum number of uses.');
                    setPromoLoading(false);
                    return;
                }
            }

            if (limitType === 'per_user' && limitValue !== null && currentUser?.uid) {
                const userOrdersSnap = await getDocs(query(
                    collection(db, 'orders'),
                    where('userId', '==', currentUser.uid),
                    where('promoCodeId', '==', promoData.id)
                ));
                const validUses = userOrdersSnap.docs.filter((snapshot) => {
                    const orderStatus = snapshot.data().status || '';
                    return orderStatus !== 'cancelled';
                }).length;

                if (validUses >= limitValue) {
                    setPromoError(
                        limitValue === 1
                            ? 'You have already used this promo code on a previous order.'
                            : `You have already used this promo code ${validUses} time${validUses !== 1 ? 's' : ''} (limit: ${limitValue}).`
                    );
                    setPromoLoading(false);
                    return;
                }
            }

            const eligibleItems = getPromoEligibleItems(promoData, orderItems);
            if (!eligibleItems.length) {
                setPromoError('This promo code does not apply to the items in your order.');
                setPromoLoading(false);
                return;
            }

            setAppliedPromo(promoData);
        } catch (err) {
            setPromoError('Failed to check promo code. Please try again.');
            console.error('Promo check error:', err);
        }
        setPromoLoading(false);
    };

    const handleRemovePromo = () => {
        setAppliedPromo(null);
        setPromoInput('');
        setPromoError('');
    };

    const orderItems = useMemo(() => order?.orderItems || [], [order?.orderItems]);
    const subtotal   = orderItems.reduce((s, item) => s + calcItemTotal(item), 0);

    useEffect(() => {
        if (!firstOrderChecked || !firstOrderEligible) return;
        if (promoInput.trim()) return;
        if (!orderItems.length) return;

        const eligibleItems = getPromoEligibleItems(WELCOME_PROMO, orderItems);
        if (!eligibleItems.length) return;

        if (appliedPromo?.id !== WELCOME_PROMO.id) {
            setAppliedPromo(WELCOME_PROMO);
        }
        setPromoError('');
    }, [firstOrderChecked, firstOrderEligible, appliedPromo?.id, promoInput, orderItems]);

    const orderItemCount = useMemo(() => getOrderItemCount(orderItems), [orderItems]);
    const maxItemsPerOrder = Number(storeSettings.deliveryConfig?.maxItemsPerOrder || 50) || 50;
    const fourWheelItemThreshold = Number(storeSettings.deliveryConfig?.fourWheelItemThreshold || 50) || 50;

    const enabledDeliveryMethods = useMemo(() => {
        const defaults = [
            { id: 'pickup', name: 'Pickup', fee: 'Free', estimatedTime: '', coverageArea: 'Store pickup', enabled: true, vehicleType: 'pickup' },
        ];
        const extras = (storeSettings.deliveryMethods || []).filter((method) => method?.enabled);
        const map = new Map();
        [...defaults, ...extras].forEach((method) => {
            if (!method) return;
            const normalized = normalizeDeliveryMethod(method);
            const key = normalized.id || normalized.name;
            if (!key) return;
            if (!map.has(key)) map.set(key, normalized);
        });
        return Array.from(map.values());
    }, [storeSettings.deliveryMethods]);

    // PayMongo is the only online payment gateway — no direct GCash/bank transfer.
    const selectedPayment = PAYMONGO_PAYMENT;

    useEffect(() => {
        if (!storeSettingsLoaded) return;
        if (!deliveryMethod && enabledDeliveryMethods.length > 0) {
            setDeliveryMethod(enabledDeliveryMethods[0].id || enabledDeliveryMethods[0].name);
        }
    }, [storeSettingsLoaded, enabledDeliveryMethods, deliveryMethod]);

    const selectedDelivery = useMemo(() => {
        return enabledDeliveryMethods.find(m => (m.id || m.name) === deliveryMethod) || null;
    }, [enabledDeliveryMethods, deliveryMethod]);

    const forcedFourWheelMethod = useMemo(() => {
        return enabledDeliveryMethods.find((method) => method.vehicleType === 'four_wheel') || null;
    }, [enabledDeliveryMethods]);

    const deliveryAutoUpgradeActive = Boolean(
        forcedFourWheelMethod
        && orderItemCount >= fourWheelItemThreshold
        && selectedDelivery?.vehicleType !== 'pickup'
    );

    useEffect(() => {
        if (!deliveryAutoUpgradeActive || !forcedFourWheelMethod) return;
        if (deliveryMethod !== forcedFourWheelMethod.id) {
            setDeliveryMethod(forcedFourWheelMethod.id);
        }
    }, [deliveryAutoUpgradeActive, forcedFourWheelMethod, deliveryMethod]);

    useEffect(() => {
        let isMounted = true;
        if (!selectedDelivery || selectedDelivery.vehicleType === 'pickup') {
            setAddressCovered(true);
            return undefined;
        }
        isAddressWithinDeliveryCoverage(address, selectedDelivery.coverageType, selectedDelivery.coverageValue).then((covered) => {
            if (isMounted) setAddressCovered(covered);
        }).catch((err) => {
            console.error('Failed to validate delivery coverage:', err);
            if (isMounted) setAddressCovered(false);
        });
        return () => { isMounted = false; };
    }, [address, selectedDelivery]);

    const activeDeliveryCoverageLabel = selectedDelivery?.vehicleType === 'pickup'
        ? 'Store pickup'
        : (selectedDelivery?.coverageLabel || 'All areas');

    const deliveryFee = selectedDelivery ? parseDeliveryFee(selectedDelivery.fee) : 0;
    const deliveryFeeLabel = selectedDelivery ? formatDeliveryFeeLabel(selectedDelivery.fee) : '₱ 0';
    const promoEligibleSubtotal = useMemo(() => {
        if (!appliedPromo) return 0;
        const eligibleItems = getPromoEligibleItems(appliedPromo, orderItems);
        return eligibleItems.reduce((sum, item) => sum + calcItemTotal(item), 0);
    }, [appliedPromo, orderItems]);

    // ── Discount calculation ─────────────────────────────────────────────────
    // First-order discount takes priority and blocks promo codes
    const firstOrderDiscountAmount = firstOrderEligible
        ? Math.round(subtotal * (Number(WELCOME_PROMO.discountValue) / 100))
        : 0;

    const promoDiscountAmount = (() => {
        if (firstOrderEligible) return 0; // no stacking
        if (!appliedPromo) return 0;
        if (promoEligibleSubtotal <= 0) return 0;
        if (appliedPromo.promotionType === 'percentage') {
            return Math.round(promoEligibleSubtotal * (parseFloat(appliedPromo.discountValue) / 100));
        }
        return Math.min(parseFloat(appliedPromo.discountValue) || 0, promoEligibleSubtotal);
    })();

    const discountAmount = firstOrderEligible ? firstOrderDiscountAmount : promoDiscountAmount;
    const grandTotal = Math.max(0, subtotal + deliveryFee - discountAmount);

    const validate = () => {
        if (!address.houseNo || !address.city || !(address.region || address.province) || !address.barangay) {
            return 'Please complete your delivery address.';
        }
        if (orderItemCount > maxItemsPerOrder) {
            return `This order has ${orderItemCount} items, which is above the current maximum of ${maxItemsPerOrder} items per order.`;
        }
        if (!selectedDelivery) {
            return 'Please select a delivery method.';
        }
        if (selectedDelivery.vehicleType !== 'pickup' && !addressCovered) {
            return `Delivery is currently available only for ${activeDeliveryCoverageLabel}. Please choose a covered address.`;
        }
        return null;
    };

    const handlePlaceOrder = async () => {
        const err = validate();
        if (err) { setSubmitError(err); return; }
        if (!order?.firestoreId) { setSubmitError('Order not loaded. Please refresh.'); return; }

        setSubmitting(true);
        setSubmitError('');

        try {
            const normalizedAddress = normalizePhilippinesAddress(address);
            const effectivePaymentName = grandTotal === 0 ? 'No Payment Needed' : PAYMONGO_PAYMENT.name;
            const newStatus = grandTotal === 0
                ? ORDER_STATUS.IN_PRODUCTION
                : ORDER_STATUS.AWAITING_PAYMENT;

            if (appliedPromo && appliedPromo.source !== 'system' && appliedPromo.id) {
                const promoRef = doc(db, 'promotions', appliedPromo.id);
                const limitType = appliedPromo.usageLimitType || 'unlimited';
                const limitValue = appliedPromo.usageLimitValue ?? null;

                try {
                    await runTransaction(db, async (transaction) => {
                        const promoSnap = await transaction.get(promoRef);
                        if (!promoSnap.exists()) throw new Error('Promo no longer exists.');

                        const latestPromo = promoSnap.data();
                        if (!isPromoCurrentlyAvailable(latestPromo)) {
                            throw new Error('This promo code is no longer active.');
                        }

                        if (limitType === 'total' && limitValue !== null) {
                            const used = latestPromo.totalUsageCount ?? 0;
                            if (used >= limitValue) {
                                throw new Error('This promo code has reached its maximum number of uses.');
                            }
                        }

                        transaction.update(promoRef, {
                            totalUsageCount: increment(1),
                            ...(limitType === 'total' && limitValue !== null && (latestPromo.totalUsageCount ?? 0) + 1 >= limitValue
                                ? { status: 'inactive' }
                                : {}),
                        });
                    });
                } catch (promoErr) {
                    setSubmitError(promoErr.message || 'Promo code could not be applied. Please try again.');
                    setSubmitting(false);
                    return;
                }
            }

            const estimatedDeliveryDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
                    .toISOString()
                    .split('T')[0];

            // ── Mark first-order discount as used on the user profile ────────
            if (firstOrderEligible && currentUser?.uid) {
                try {
                    await updateDoc(doc(db, 'users', currentUser.uid), {
                        hasUsedFirstOrderDiscount: true,
                        updatedAt: serverTimestamp(),
                    });
                } catch (err) {
                    // Non-fatal — log but don't block the order
                    console.error('Failed to mark first-order discount as used:', err);
                }
            }

           // const orderRef = doc(db, 'orders', order.firestoreId);

           /* // ╔══════════════════════════════════════════════════════════════════╗
            // ║  TESTING BYPASS — Remove this block and uncomment ORIGINAL      ║
            // ║  block below after upgrading to Blaze plan.                     ║
            // ╚══════════════════════════════════════════════════════════════════╝
            
            // We update the DB with exactly what the original code did to avoid security rule errors.
            await updateDoc(orderRef, {
                paymentMethod:              effectivePaymentName,
                paymentGateway:             grandTotal > 0 ? 'paymongo' : '',
                paymentStatus:              grandTotal > 0 ? 'pending' : 'not_required',
                paymentConfirmed:           false,
                deliveryMethod:             selectedDelivery?.displayName || selectedDelivery?.name || '',
                deliveryFee,
                deliveryAddress:            normalizedAddress,
                promoCode:                  appliedPromo?.promoCode    || '',
                promoCodeId:                appliedPromo?.id           || '',
                discountAmount:             discountAmount             || 0,
                discountType:               firstOrderEligible ? 'firstOrder' : (appliedPromo?.promotionType || ''),
                firstOrderDiscount:         firstOrderEligible,
                estimatedDeliveryDate,
                orderTotal:                 grandTotal,
                updatedAt:                  serverTimestamp(),
            });

            const { error: statusErr } = await updateOrderStatus(order.firestoreId, newStatus, {
                userId:                currentUser?.uid   || '',
                customerName:          order.customerName || '',
                customerEmail:         order.customerEmail || '',
                orderId:               order.orderId,
                orderTotal:            grandTotal,
                existingStatusHistory: order.statusHistory || [],
                note: grandTotal === 0
                    ? 'Customer checked out with a fully discounted order — moved to production'
                    : `Testing Bypass: Customer order placed, simulating PayMongo success`,
            });

            if (statusErr) {
                setSubmitError('Order placed but status update failed: ' + statusErr);
                setSubmitting(false);
                return;
            }

            // We SKIP the actual PayMongo cloud function call here.
            console.log('[CHECKOUT BYPASS] Skipping PayMongo URL generation for testing.');

            // Delete "Order Approved" notification
            try {
                const approvedNotifsQ = query(
                    collection(db, 'notifications', currentUser.uid, 'items'),
                    where('title', '==', 'Order Approved'),
                    where('orderId', '==', order.orderId)
                );
                const approvedSnap = await getDocs(approvedNotifsQ);
                await Promise.all(approvedSnap.docs.map(d => deleteDoc(d.ref)));
            } catch (err) {
                console.warn('Could not delete Order Approved notification:', err);
            }

            // Navigate to confirmation page, but force the status to IN_PRODUCTION 
            // so the user sees a "success" screen instead of "awaiting payment".
            navigate('/order-confirmation', {
                state: {
                    orderId:        order.orderId,
                    orderFirestoreId: order.firestoreId,
                    orderItems,
                    grandTotal,
                    paymentMethod:  effectivePaymentName,
                    newStatus:      ORDER_STATUS.IN_PRODUCTION, 
                    estimatedDeliveryDate,
                }
            });
            // ╔══════════════════════════════════════════════════════════════════╗
            // ║  END TESTING BYPASS                                             ║
            // ╚══════════════════════════════════════════════════════════════════╝
*/
             // ══════════════════════════════════════════════════════════════════
               // ORIGINAL CODE — Uncomment after upgrading to Blaze plan
               // ══════════════════════════════════════════════════════════════════

            const orderRef = doc(db, 'orders', order.firestoreId);
            await updateDoc(orderRef, {
                paymentMethod:              effectivePaymentName,
                paymentGateway:             grandTotal > 0 ? 'paymongo' : '',
                paymentStatus:              grandTotal > 0 ? 'pending' : 'not_required',
                paymentConfirmed:           false,
                deliveryMethod:             selectedDelivery?.displayName || selectedDelivery?.name || '',
                deliveryFee,
                deliveryAddress:            normalizedAddress,
                promoCode:                  appliedPromo?.promoCode    || '',
                promoCodeId:                appliedPromo?.id           || '',
                discountAmount:             discountAmount             || 0,
                discountType:               firstOrderEligible ? 'firstOrder' : (appliedPromo?.promotionType || ''),
                firstOrderDiscount:         firstOrderEligible,
                estimatedDeliveryDate,
                orderTotal:                 grandTotal,
                updatedAt:                  serverTimestamp(),
            });

            if (grandTotal === 0) {
                const { error: statusErr } = await updateOrderStatus(order.firestoreId, newStatus, {
                    userId:                currentUser?.uid   || '',
                    customerName:          order.customerName || '',
                    customerEmail:         order.customerEmail || '',
                    orderId:               order.orderId,
                    orderTotal:            grandTotal,
                    existingStatusHistory: order.statusHistory || [],
                    note: 'Customer checked out with a fully discounted order — moved to production',
                });

                if (statusErr) {
                    setSubmitError('Order placed but status update failed: ' + statusErr);
                    setSubmitting(false);
                    return;
                }
            }

            // ── PayMongo: reuse or create checkout link, then redirect (no duplicate charge) ──
            if (grandTotal > 0) {
                // Instantly update the local state so the pending banner shows up without waiting for onSnapshot bounce
                setOrder(prev => ({ ...prev, paymentStatus: 'pending' }));
                
                if (!functions) {
                    throw new Error('Payment service is not available. Please try again later.');
                }
                
                // Open new tab synchronously to bypass popup blockers
                const paymentWindow = window.open('', '_blank');
                if (paymentWindow) {
                    paymentWindow.document.write('<div style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;color:#333;">Loading secure payment...</div>');
                }

                let checkoutUrl;
                try {
                    const createCheckout = httpsCallable(functions, 'createPaymongoCheckout');
                    const { data } = await createCheckout({ orderFirestoreId: order.firestoreId });
                    if (!data?.checkoutUrl) {
                        throw new Error('PayMongo checkout URL was not returned.');
                    }
                    checkoutUrl = data.checkoutUrl;
                } catch (err) {
                    if (paymentWindow) paymentWindow.close();
                    throw err;
                }

                if (paymentWindow) {
                    paymentWindow.location.href = checkoutUrl;
                } else {
                    // Fallback if popup blocker blocked the synchronous open too
                    window.location.href = checkoutUrl;
                }
                setSubmitError('');
                setSubmitting(false);
                return;
            }

            // ── Delete "Order Approved" notification once order is placed ─────────
            try {
                const approvedNotifsQ = query(
                    collection(db, 'notifications', currentUser.uid, 'items'),
                    where('title', '==', 'Order Approved'),
                    where('orderId', '==', order.orderId)
                );
                const approvedSnap = await getDocs(approvedNotifsQ);
                await Promise.all(approvedSnap.docs.map(d => deleteDoc(d.ref)));
            } catch (err) {
                console.warn('Could not delete Order Approved notification:', err);
            }

            navigate('/order-confirmation', {
                state: {
                    orderId:        order.orderId,
                    orderFirestoreId: order.firestoreId,
                    orderItems,
                    grandTotal,
                    paymentMethod:  effectivePaymentName,
                    newStatus,
                    estimatedDeliveryDate,
                }
            });

             // ══════════════════════════════════════════════════════════════════
               // END ORIGINAL CODE
               // ══════════════════════════════════════════════════════════════════
        } catch (err) {
            console.error('Checkout error:', err);
            setSubmitError('An unexpected error occurred: ' + err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (orderLoading) return (
        <div style={{ width: '100%', backgroundColor: '#ffffff' }}>
            <div className="checkout-page">
                <div className="checkout-loading">
                    <div className="checkout-spinner"/>
                    <p>Loading your order…</p>
                </div>
            </div>
        </div>
    );

    if (orderError) return (
        <div style={{ width: '100%', backgroundColor: '#ffffff' }}>
            <div className="checkout-page">
                <div className="checkout-error-state">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#c62828" strokeWidth="1.5">
                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    <p>{orderError}</p>
                    <button onClick={() => navigate('/account/purchase-history')}>Go to Purchase History</button>
                </div>
            </div>
        </div>
    );

    return (
        <div style={{ width: '100%', backgroundColor: '#ffffff' }}>
            <div className="checkout-page">
                <h1 className="checkout-title">Check Out</h1>

                <div className="checkout-grid">

                    {/* ── LEFT: Forms ── */}
                    <div className="checkout-forms">

                        <div className="checkout-order-notice">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>
                            <div>
                                <strong>Order {order.orderId} has been approved!</strong>
                                <span> Complete your details below to proceed.</span>
                            </div>
                        </div>

                        {/* ── First-order welcome banner ── */}
                        {firstOrderChecked && firstOrderEligible && (
                            <div className="checkout-first-order-banner">
                                <div className="checkout-first-order-banner-icon">🎉</div>
                                <div className="checkout-first-order-banner-text">
                                    <strong>Welcome! Your first-order discount is ready.</strong>
                                    <span>A 15% discount has been automatically applied to this order.</span>
                                </div>
                                <div className="checkout-first-order-badge">15% OFF</div>
                            </div>
                        )}

                        {/* ── Delivery Address ── */}
                        <div className="checkout-section">
                            <div className="checkout-section-header">
                                <h2>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                                    </svg>
                                    Delivery Address
                                </h2>
                                {!editingAddress && (
                                    <button className="checkout-edit-btn" onClick={() => setEditingAddress(true)}>Edit</button>
                                )}
                            </div>

                            {!editingAddress ? (
                                <div className="checkout-address-display">
                                    {formatAddress(address) || (
                                        <span className="checkout-no-address">
                                            No address saved.{' '}
                                            <button onClick={() => setEditingAddress(true)}>Add one</button>
                                        </span>
                                    )}
                                    {savedAddresses.length > 1 && (
                                        <select
                                            className="checkout-address-select"
                                            onChange={e => {
                                                const sel = savedAddresses[Number(e.target.value)];
                                                if (sel) {
                                                    setAddress(normalizePhilippinesAddress({
                                                        houseNo:sel.houseNo||'',
                                                        barangay:sel.barangay||'',
                                                        city:sel.city||'',
                                                        region:sel.region||'',
                                                        province:sel.province||'',
                                                        zipCode:sel.zipCode||'',
                                                        landmark:sel.landmark||'',
                                                    }));
                                                }
                                            }}
                                            defaultValue=""
                                        >
                                            <option value="" disabled>Switch to another saved address…</option>
                                            {savedAddresses.map((a, i) => (
                                                <option key={i} value={i}>{formatAddress(a) || `Address ${i + 1}`}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            ) : (
                                <div className="checkout-address-form">
                                    <div className="checkout-form-row">
                                        <div className="checkout-field">
                                            <label>House No. / Street <span className="req">*</span></label>
                                            <input type="text" placeholder="123 Rizal St." value={address.houseNo} onChange={e => setAddress(p => ({...p, houseNo:e.target.value}))} />
                                        </div>
                                    </div>
                                    <div className="checkout-form-row">
                                        <div className="checkout-field">
                                            <label>Region <span className="req">*</span></label>
                                            <select
                                                value={address.region || ''}
                                                onChange={e => setAddress((prev) => normalizePhilippinesAddress({ ...prev, region: e.target.value, province: '', city: '', barangay: '', zipCode: '' }))}
                                            >
                                                <option value="">Select a region</option>
                                                {regionOptions.map((region) => (
                                                    <option key={region.code} value={region.label}>{region.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="checkout-field">
                                            <label>Province <span className="req">*</span></label>
                                            <select
                                                value={address.province || ''}
                                                onChange={e => setAddress((prev) => normalizePhilippinesAddress({ ...prev, province: e.target.value, city: '', barangay: '', zipCode: '' }))}
                                                disabled={!address.region}
                                            >
                                                <option value="">{address.region ? 'Select a province' : 'Select a region first'}</option>
                                                {provinceOptions.map((province) => (
                                                    <option key={province.code} value={province.label}>{province.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="checkout-form-row">
                                        <div className="checkout-field">
                                            <label>City / Municipality <span className="req">*</span></label>
                                            <select
                                                value={address.city}
                                                onChange={e => setAddress((prev) => ({ ...prev, city: e.target.value, barangay: '', zipCode: '' }))}
                                                disabled={!address.province}
                                            >
                                                <option value="">{address.province ? 'Select a city' : 'Select a province first'}</option>
                                                {cityOptions.map((city) => (
                                                    <option key={city.code} value={city.label}>{city.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="checkout-field">
                                            <label>Barangay <span className="req">*</span></label>
                                            <select
                                                value={address.barangay}
                                                onChange={e => setAddress(p => ({...p, barangay:e.target.value}))}
                                                disabled={!address.city}
                                            >
                                                <option value="">{address.city ? 'Select a barangay' : 'Select a city first'}</option>
                                                {barangayOptions.map((barangay) => (
                                                    <option key={barangay.code} value={barangay.label}>{barangay.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="checkout-form-row">
                                        <div className="checkout-field">
                                            <label>Zip Code</label>
                                            <input type="text" placeholder="1630" value={address.zipCode} onChange={e => setAddress(p => ({...p, zipCode:e.target.value}))} />
                                        </div>
                                    </div>
                                    <div className="checkout-field">
                                        <label>Landmark <span className="opt">(Optional)</span></label>
                                        <input type="text" placeholder="Nearest Mercury Drug" value={address.landmark} onChange={e => setAddress(p => ({...p, landmark:e.target.value}))} />
                                    </div>
                                        <p className="checkout-gcash-hint">
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <path d="M12 6V6M12 10V18M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                            {addressCovered
                                                ? `This delivery method currently covers ${activeDeliveryCoverageLabel}.`
                                                : `This address is outside the configured ${activeDeliveryCoverageLabel} delivery coverage.`}
                                        </p>
                                    <button className="checkout-save-addr-btn" onClick={() => setEditingAddress(false)}>Save Address</button>
                                </div>
                            )}
                        </div>

                        {/* ── Delivery Method ── */}
                        <div className="checkout-section">
                            <h2>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
                                    <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                                </svg>
                                Delivery Method
                            </h2>
                            <div className="checkout-radio-group">
                                {enabledDeliveryMethods.length === 0 ? (
                                    <div className="checkout-empty-note">No delivery methods available right now.</div>
                                ) : enabledDeliveryMethods.map((method) => {
                                    const value = method.id || method.name;
                                    const note = buildDeliveryNote(method) || 'Delivery details will be confirmed after checkout.';
                                    const disabled = deliveryAutoUpgradeActive && method.vehicleType !== 'four_wheel' && method.vehicleType !== 'pickup';
                                    return (
                                        <label key={value} className={`checkout-radio-option ${deliveryMethod === value ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}>
                                            <input type="radio" name="deliveryMethod" value={value} checked={deliveryMethod === value} onChange={e => setDeliveryMethod(e.target.value)} disabled={disabled} />
                                            <span className="checkout-radio-icon">{value === 'pickup' ? '🏪' : '🚚'}</span>
                                            <div className="checkout-radio-text">
                                                <span className="checkout-radio-label">{method.displayName || method.name}</span>
                                                <span className="checkout-radio-note">
                                                    {disabled
                                                        ? `Automatically upgraded to 4-wheel delivery for orders with ${fourWheelItemThreshold}+ items.`
                                                        : note}
                                                </span>
                                            </div>
                                            {deliveryMethod === value && (
                                                <svg className="checkout-radio-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                    <polyline points="20 6 9 17 4 12"/>
                                                </svg>
                                            )}
                                        </label>
                                    );
                                })}
                            </div>
                        </div>

                        {/* ── PayMongo (only online payment gateway) ── */}
                        {grandTotal > 0 && (
                            <div className="checkout-section checkout-gcash-section">
                                <h2>
                                    <span className="checkout-gcash-badge">P</span>
                                    PayMongo Secure Checkout
                                </h2>
                                <div className="checkout-gcash-body">
                                    <div className="checkout-gcash-right" style={{ width: '100%' }}>
                                        <p className="checkout-gcash-instructions">
                                            After you click <strong>Place Order</strong>, you&apos;ll be redirected to PayMongo to pay securely.
                                            Supported methods include GCash, Maya, credit/debit cards, and other e-wallets — all handled by PayMongo.
                                        </p>
                                        <p className="checkout-gcash-hint">
                                            If your connection drops after paying, don&apos;t pay again. Open your order confirmation and use
                                            <strong> Sync Payment</strong>, or wait a moment — our system confirms payments automatically.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {grandTotal === 0 && (
                            <div className="checkout-cod-notice">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                                </svg>
                                <p>Your order total is fully covered by discounts — no online payment is required.</p>
                            </div>
                        )}

                    </div>

                    {/* ── RIGHT: Order Summary ── */}
                    <div className="checkout-summary">
                        <div className="checkout-summary-inner">
                            <h2 className="checkout-summary-title">Your Order</h2>
                            <p className="checkout-summary-id">Order #{order.orderId}</p>

                            <div className="checkout-summary-items">
                                {orderItems.map((item, idx) => {
                                    const itemTotal = calcItemTotal(item);
                                    const quantities = item.quantities || {};
                                    const computedQty = Object.values(quantities).reduce((s, q) => s + (q || 0), 0);
                                    const displayQty = computedQty > 0 ? computedQty : Number(item.quantity || 0);
                                    const sizedQtys = SIZE_ORDER.filter(s => (quantities[s] || 0) > 0);
                                    const isPoloJersey = NO_COLOR_SUBCATEGORIES.includes(item.subcategory);
                                    const isOpen    = sizeBreakdownOpen === idx;
                                    return (
                                        <div key={item.id || idx} className="checkout-summary-item">
                                            <div className="checkout-summary-item-top">
                                                {item.image && <img src={item.image} alt={item.name} className="checkout-summary-thumb" />}
                                                <div className="checkout-summary-item-info">
                                                    <h4>Item #{idx + 1}</h4>
                                                    <p><strong>Type:</strong> {item.type}</p>
                                                    {!isPoloJersey && <p><strong>Color:</strong> {item.color}</p>}
                                                    {!isPoloJersey && <p><strong>Shape:</strong> {item.designShape}</p>}
                                                    {!isPoloJersey && <p><strong>Locations:</strong> {item.printLocation}</p>}
                                                    <p><strong>Total Qty:</strong> {displayQty} pcs</p>
                                                </div>
                                                <div className="checkout-summary-item-price">₱ {itemTotal.toLocaleString()}</div>
                                            </div>
                                            {sizedQtys.length > 0 && (
                                                <button className="checkout-size-toggle" onClick={() => setSizeBreakdownOpen(isOpen ? null : idx)}>
                                                    {isOpen ? '▲ Hide' : '▼ View'} Size Breakdown
                                                </button>
                                            )}
                                            {isOpen && (
                                                <div className="checkout-size-breakdown">
                                                    <table>
                                                        <thead><tr>{sizedQtys.map(s=><th key={s}>{SIZE_LABELS[s]}</th>)}<th>Total</th></tr></thead>
                                                        <tbody><tr>{sizedQtys.map(s=><td key={s}>{quantities[s]}</td>)}<td><strong>{displayQty}</strong></td></tr></tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* ── Promo code — hidden when first-order discount is active ── */}
                            {!firstOrderEligible && (
                                <div className="checkout-promo-section">
                                    <h3 className="checkout-promo-title">Promo Code</h3>
                                    {appliedPromo ? (
                                        <div className="checkout-promo-applied">
                                            <div className="checkout-promo-applied-info">
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2d7a3a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                                                <div>
                                                    <strong>{appliedPromo.promoCode}</strong>
                                                    <span> — {appliedPromo.promotionName}</span>
                                                    <p className="checkout-promo-discount-label">
                                                        {appliedPromo.promotionType === 'percentage'
                                                            ? `${appliedPromo.discountValue}% off → −₱ ${promoDiscountAmount.toLocaleString()}`
                                                            : `−₱ ${promoDiscountAmount.toLocaleString()} off`
                                                        }
                                                    </p>
                                                </div>
                                            </div>
                                            <button className="checkout-promo-remove" onClick={handleRemovePromo}>✕</button>
                                        </div>
                                    ) : (
                                        <div className="checkout-promo-input-row">
                                            <input
                                                type="text"
                                                className="checkout-promo-input"
                                                placeholder="Enter promo code"
                                                value={promoInput}
                                                onChange={e => { setPromoInput(e.target.value.toUpperCase()); setPromoError(''); }}
                                                onKeyDown={e => e.key === 'Enter' && handleApplyPromo()}
                                            />
                                            <button className="checkout-promo-apply-btn" onClick={handleApplyPromo} disabled={promoLoading}>
                                                {promoLoading ? '…' : 'Apply'}
                                            </button>
                                        </div>
                                    )}
                                    {promoError && <p className="checkout-promo-error">{promoError}</p>}
                                </div>
                            )}

                            {/* ── Totals ── */}
                            <div className="checkout-summary-totals">
                                <div className="checkout-total-row">
                                    <span>Order Subtotal</span>
                                    <span>₱ {subtotal.toLocaleString()}</span>
                                </div>
                                <div className="checkout-total-row">
                                    <span>Delivery Fee</span>
                                    <span>{deliveryFeeLabel}</span>
                                </div>
                                {firstOrderEligible && (
                                    <div className="checkout-total-row checkout-total-discount checkout-total-first-order">
                                        <span>
                                            🎉 First Order Discount
                                            <span className="checkout-first-order-tag">15% OFF</span>
                                        </span>
                                        <span>−₱ {firstOrderDiscountAmount.toLocaleString()}</span>
                                    </div>
                                )}
                                {!firstOrderEligible && appliedPromo && (
                                    <div className="checkout-total-row checkout-total-discount">
                                        <span>Promo ({appliedPromo.promoCode})</span>
                                        <span>−₱ {promoDiscountAmount.toLocaleString()}</span>
                                    </div>
                                )}
                                <div className="checkout-total-row checkout-total-grand">
                                    <span>Total</span>
                                    <strong>{grandTotal === 0 ? <span className="checkout-total-free">FREE</span> : `₱ ${grandTotal.toLocaleString()}`}</strong>
                                </div>
                            </div>

                            {submitError && (
                                <div className="checkout-error-msg">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                                    </svg>
                                    {submitError}
                                </div>
                            )}

                            {order?.paymentStatus === 'pending' && (
                                <div className="checkout-pending-msg" style={{ backgroundColor: '#e3f2fd', color: '#0d47a1', padding: '12px', borderRadius: '8px', marginBottom: '16px', fontSize: '0.9rem', textAlign: 'center' }}>
                                    <strong style={{ display: 'block', marginBottom: '4px' }}>Waiting for Payment...</strong>
                                    We've opened the payment link in a new tab. Once you complete the payment, this page will automatically update!
                                </div>
                            )}

                            <button className="checkout-place-order-btn" onClick={handlePlaceOrder} disabled={submitting}>
                                {submitting ? (
                                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                        <span className="spinner-small" style={{ width: '16px', height: '16px', border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></span>
                                        Please wait...
                                    </span>
                                ) : order?.paymentStatus === 'pending' ? (
                                    <>Re-open Payment Link <span className="arrow">→</span></>
                                ) : (
                                    <>Place Order <span className="arrow">→</span></>
                                )}
                            </button>
                            <p className="checkout-submit-note">
                                By placing your order, you confirm that all details are correct and the design is approved for printing.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
